import { apiRequest } from '@/domains/api';
import {
  DEFAULT_DESIGN_SETTINGS,
  type IMailDesign,
  type IMailDesignDraft,
  type IMailDesignPreviewRequest,
  type IMailDesignSettings,
  type IMailDesignSummary,
  type IMailDesignVersion,
  type MailDesignCode
} from './mail-design-contracts';
import type { IMailPreview } from './mail-template-contracts';

/**
 * survey-api's email designs (SR-523 stage 6), every route `mail.templates`. A draft's save quotes
 * its revision in `If-Match` ("0" for none), a publication the latest version number; a stale one
 * is 409 `revision-conflict`. Settings survey-api refuses are 400 `invalid-body`, `problems[]`
 * targeting the setting (`colors.accent`, `band.logoUrl`).
 */

const MAIL_DESIGNS = '/api/v1/admin/mail-designs';

const designPath = (code: string) => `${MAIL_DESIGNS}/${encodeURIComponent(code)}`;

const quoted = (revision: number) => `"${revision}"`;

/** Settings with every field present — an older or partial answer filled from the defaults. */
export function designSettingsOf(settings: Nullable<Partial<IMailDesignSettings>> | undefined): IMailDesignSettings {
  const given = settings ?? {};
  return {
    band: { ...DEFAULT_DESIGN_SETTINGS.band, ...(given.band ?? {}) },
    colors: { ...DEFAULT_DESIGN_SETTINGS.colors, ...(given.colors ?? {}) },
    font: given.font || DEFAULT_DESIGN_SETTINGS.font,
    width: typeof given.width === 'number' ? given.width : DEFAULT_DESIGN_SETTINGS.width,
    footer: given.footer?.doc ? given.footer : DEFAULT_DESIGN_SETTINGS.footer
  };
}

function versionOf(version: IMailDesignVersion): IMailDesignVersion {
  return { ...version, note: version.note ?? '', settings: designSettingsOf(version.settings) };
}

type Pinned = Nullable<{ version: number; effectiveAt?: Nullable<string>; settings?: IMailDesignSettings }>;

/** A version named by its number, or the version itself. */
function pinnedOf(value: unknown): Pinned {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return { version: value };
  const entry = value as { version?: number; effectiveAt?: Nullable<string>; settings?: IMailDesignSettings };
  return {
    version: entry.version ?? 0,
    effectiveAt: entry.effectiveAt ?? null,
    settings: entry.settings ? designSettingsOf(entry.settings) : undefined
  };
}

export const mailDesignsApi = {
  async list(signal?: AbortSignal): Promise<IMailDesignSummary[]> {
    const answer = await apiRequest<IMailDesignSummary[] | { designs?: IMailDesignSummary[] }>(MAIL_DESIGNS, { signal });
    const rows = Array.isArray(answer) ? answer : (answer.designs ?? []);
    return rows.map((row) => ({
      ...row,
      current: pinnedOf(row.current),
      scheduled: pinnedOf(row.scheduled),
      draft: Boolean(row.draft)
    }));
  },

  async get(code: MailDesignCode, signal?: AbortSignal): Promise<IMailDesign> {
    const answer = await apiRequest<Omit<IMailDesign, 'current'> & { current?: unknown }>(designPath(code), { signal });
    const versions = (answer.versions ?? []).map(versionOf);
    return {
      ...answer,
      code: answer.code ?? code,
      // A number here; the list names the version itself.
      current:
        pinnedOf(answer.current)?.version ??
        versions.find((version) => version.status === 'current')?.version ??
        0,
      fonts: answer.fonts ?? [],
      settings: designSettingsOf(answer.settings),
      versions,
      draft: answer.draft
        ? { ...answer.draft, note: answer.draft.note ?? '', settings: designSettingsOf(answer.draft.settings) }
        : null
    };
  },

  async saveDraft(
    code: MailDesignCode,
    revision: number,
    draft: { settings: IMailDesignSettings; note: string }
  ): Promise<IMailDesignDraft> {
    const answer = await apiRequest<{ draft?: IMailDesignDraft } & Partial<IMailDesignDraft>>(`${designPath(code)}/draft`, {
      method: 'PUT',
      body: draft,
      headers: { 'If-Match': quoted(revision) }
    });
    const saved = (answer.draft ?? answer) as IMailDesignDraft;
    return { ...saved, note: saved.note ?? '', settings: designSettingsOf(saved.settings) };
  },

  /** The draft gone: the editor goes back to the current version. */
  discardDraft(code: MailDesignCode, revision: number): Promise<void> {
    return apiRequest<void>(`${designPath(code)}/draft`, { method: 'DELETE', headers: { 'If-Match': quoted(revision) } });
  },

  /** The draft as the next version, from `effectiveAt` (null: now). */
  publish(
    code: MailDesignCode,
    latest: number,
    publication: { effectiveAt: Nullable<string>; note: string }
  ): Promise<unknown> {
    return apiRequest<unknown>(`${designPath(code)}/publish`, {
      method: 'POST',
      body: publication,
      headers: { 'If-Match': quoted(latest) }
    });
  },

  withdraw(code: MailDesignCode, version: number): Promise<unknown> {
    return apiRequest<unknown>(`${designPath(code)}/versions/${version}/withdraw`, { method: 'POST' });
  },

  /** A template — the submission confirmation unless named — drawn with these settings. */
  async preview(code: MailDesignCode, request: IMailDesignPreviewRequest, signal?: AbortSignal): Promise<IMailPreview> {
    const answer = await apiRequest<IMailPreview>(`${designPath(code)}/preview`, { method: 'POST', body: request, signal });
    return { ...answer, preheader: answer.preheader ?? '' };
  }
};
